import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import type { ReactDatePickerProps } from 'react-datepicker';
import moment from 'moment';
import useDropdown from '../../../components/hooks/useDropdown';
import DatePicker from '../DatePicker';
import { Box, Button, Input, Text } from '../index';
import type { UseFloatingOptions } from '@floating-ui/react';

export interface DateRangePickerProps
  extends Omit<ReactDatePickerProps, 'onChange'> {
  timeZone?: string | undefined;
  start?: string | number | Date | null;
  end?: string | number | Date | null;
  inputStyle?: React.CSSProperties;
  onChange: (val: [Date, Date] | null) => void;
  placement?: UseFloatingOptions['placement'];
  isControlled?: boolean;
}

interface PresetOption {
  id: number;
  value: number;
  unit: 'month' | 'isoWeek' | 'week' | '';
  label: string;
}

const PRESET_OPTIONS: PresetOption[] = [
  { id: 0, value: 0, unit: '', label: 'Custom Range' },
  { id: 1, value: -1, unit: 'month', label: 'Last month' },
  { id: 2, value: -1, unit: 'isoWeek', label: 'Last week' },
  { id: 3, value: 0, unit: 'month', label: 'This month' },
  { id: 4, value: 0, unit: 'isoWeek', label: 'This week' },
  { id: 5, value: 1, unit: 'month', label: 'Next month' },
  { id: 6, value: 1, unit: 'isoWeek', label: 'Next week' },
];

const DateRangePicker = ({
  start = null,
  end = null,
  timeZone = 'UTC',
  inputStyle = {
    border: 'none',
  },
  onChange,
  isClearable = true,
  placement,
  disabled,
  isControlled,
}: DateRangePickerProps): JSX.Element => {
  const [startDate, setStartDate] = useState(start ? new Date(start) : null);
  const [endDate, setEndDate] = useState(end ? new Date(end) : null);
  const [preset, setPreset] = useState(0);
  const [dropdownRoot, setDropDownRoot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const el = document.getElementById('dropdown-root');
    setDropDownRoot(el);
  }, []);

  const { setMenuRef, setTargetRef, styles, isVisible, setVisibility } =
    useDropdown({
      closeOnContentClick: false,
      closeOnOutsideClick: false,
      floatProps: { placement },
    });

  useEffect(() => {
    if (isControlled) {
      setStartDate((curStart) => {
        const newDate = start ? new Date(start) : null;

        return newDate
          ? newDate.valueOf() === curStart?.valueOf()
            ? curStart
            : newDate
          : null;
      });
      setEndDate((curEnd) => {
        const newDate = end ? new Date(end) : null;

        return newDate
          ? newDate.valueOf() === curEnd?.valueOf()
            ? curEnd
            : newDate
          : null;
      });
    }
  }, [isControlled, start, end]);

  useEffect(() => {
    const currentOption = PRESET_OPTIONS[preset];
    const v: moment.DurationInputArg1 = currentOption.value;
    const u: moment.unitOfTime.DurationConstructor =
      currentOption.unit as moment.unitOfTime.DurationConstructor;
    if (preset !== 0) {
      const result = moment().add(v, u);
      setStartDate(result.startOf(u).toDate());
      setEndDate(result.endOf(u).toDate());
    }
  }, [preset]);

  const changeStartDate = (value: Date) => {
    setStartDate(value);
    setPreset(0);
  };

  const changeEndDate = (value: Date) => {
    setEndDate(value);
    setPreset(0);
  };

  const confirmRange = () => {
    setVisibility(false);
    const value: [Date, Date] | null =
      startDate && endDate ? [startDate, endDate] : null;
    onChange(value);
  };

  const clearRange = () => {
    if (!isClearable) return;
    setVisibility(false);
    setPreset(0);
    setStartDate(null);
    setEndDate(null);
    onChange(null);
  };

  const formattedStartDate = `${moment(startDate).format('YYYY-MM-DD')}`;
  const formattedEndDate = `${moment(endDate).format('YYYY-MM-DD')}`;
  const rangeText =
    startDate && endDate ? formattedStartDate + '  ~  ' + formattedEndDate : '';

  return (
    <Box className={'custom-date-range-picker'}>
      <Box display="flex" flexDirection="row" position="relative">
        <Box ref={setTargetRef}>
          <Input
            border={inputStyle.border}
            borderTopRightRadius={4}
            borderBottomRightRadius={4}
            minWidth={200}
            name="preset"
            value={rangeText}
            onClick={() => setVisibility(!isVisible)}
            onChange={() => ({})}
            autoComplete="off"
            disabled={disabled}
          />
        </Box>
        {dropdownRoot &&
          ReactDOM.createPortal(
            <div ref={setMenuRef} style={styles}>
              {isVisible && (
                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="stretch"
                  position="absolute"
                  overflow="hidden"
                  borderRadius={4}
                  flexShrink={0}
                  zIndex={999}
                  top={1}
                  left={-300}
                >
                  <Box
                    minWidth={140}
                    paddingX={2}
                    paddingY={2}
                    backgroundColor="#fff"
                    borderWidth={1}
                    borderColor="#c2c2c2"
                    borderStyle="solid"
                    borderRadius={4}
                  >
                    {PRESET_OPTIONS.map((x: PresetOption) => {
                      return (
                        <Box
                          style={{ cursor: 'pointer' }}
                          lineHeight={2}
                          paddingX={3}
                          paddingY={2}
                          backgroundColor={
                            preset === x.id ? '#216ba5' : 'transparent'
                          }
                          minHeight={32}
                          key={x.id}
                          onClick={() => {
                            setPreset(x.id);
                          }}
                        >
                          <Text color={preset === x.id ? '#fff' : '#000'}>
                            {x.label}
                          </Text>
                        </Box>
                      );
                    })}
                  </Box>
                  <Box
                    borderWidth={1}
                    backgroundColor="#fff"
                    borderStyle="solid"
                    borderColor="#c2c2c2"
                    borderRadius={4}
                    overflow="hidden"
                  >
                    <Box
                      className="date-range-pickers"
                      display="flex"
                      flexDirection="row"
                    >
                      <Box>
                        <DatePicker
                          shouldCloseOnSelect={false}
                          onChange={(date: Date) => changeStartDate(date)}
                          selectsStart
                          startDate={startDate}
                          endDate={endDate}
                          selected={startDate}
                          showTime={false}
                          inline
                          isClearable={false}
                          noButton={true}
                          timeZone={timeZone}
                        />
                      </Box>
                      <Box>
                        <DatePicker
                          onChange={(date: Date) => changeEndDate(date)}
                          selectsEnd
                          startDate={startDate}
                          endDate={endDate}
                          selected={endDate}
                          showTime={false}
                          inline
                          isClearable={false}
                          noButton={true}
                          timeZone={timeZone}
                        />
                      </Box>
                    </Box>
                    <Box
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-between"
                      padding={2}
                      borderTop={'1px solid #c2c2c2'}
                      backgroundColor="#fff"
                    >
                      {isClearable && (
                        <Button
                          variant="secondary"
                          flexGrow={1}
                          maxWidth={100}
                          flexBasis={0}
                          onPress={() => clearRange()}
                        >
                          Clear
                        </Button>
                      )}
                      <Button
                        variant="primary"
                        flexGrow={1}
                        maxWidth={100}
                        flexBasis={0}
                        onPress={confirmRange}
                      >
                        OK
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
            </div>,
            dropdownRoot,
          )}
      </Box>
    </Box>
  );
};

export default DateRangePicker;
